import type { IEventBus } from "@/domains/events"
import { LockVotedEvent } from "@/domains/lock/events"
import { ManagerModeChangedEvent } from "@/domains/manager/events"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import type { SelectedPowerStore } from "./selected-power-store"

/**
 * SelectedPowerOrchestrator - Event handler for SelectedPowerStore
 *
 * Subscribes to wallet events and clears selected NFTs when wallet switches:
 * - Wallet connect/disconnect → Clear selected NFT IDs
 *
 * This ensures that when a user switches wallets, the old NFT selection
 * doesn't persist, preventing cached strategy values from showing incorrect data.
 */
export class SelectedPowerOrchestrator {
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private managerModeChangedHandler: ((event: ManagerModeChangedEvent) => void) | undefined
  private lockVotedHandler: ((event: LockVotedEvent) => void) | undefined

  constructor(
    private readonly selectedPowerStore: SelectedPowerStore,
    private readonly eventBus: IEventBus,
  ) {}

  initialize(): void {
    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    // Subscribe to manager mode changes
    this.managerModeChangedHandler = this.handleManagerModeChanged.bind(this)
    this.eventBus.subscribe(ManagerModeChangedEvent.TYPE, this.managerModeChangedHandler)

    // Subscribe to lock voted events to deselect voted NFTs
    this.lockVotedHandler = this.handleLockVoted.bind(this)
    this.eventBus.subscribe(LockVotedEvent.TYPE, this.lockVotedHandler)
  }

  private handleWalletConnect(_event: WalletConnectedEvent): void {
    // Clear selected NFTs when wallet connects (new wallet, new NFTs)
    this.selectedPowerStore.setSelectedNfts([])
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    // Clear selected NFTs when wallet disconnects
    this.selectedPowerStore.setSelectedNfts([])
  }

  private handleManagerModeChanged(_event: ManagerModeChangedEvent): void {
    // Clear selected NFTs when manager mode changes (switching between wallet and manager mode)
    this.selectedPowerStore.setSelectedNfts([])
  }

  private handleLockVoted(event: LockVotedEvent): void {
    // Get current selected NFT IDs
    const currentSelectedIds = this.selectedPowerStore.getSelectedNftIds()

    // Filter out the voted NFT IDs
    const remainingSelectedIds = currentSelectedIds.filter((id) => !event.lockIds.includes(id))

    // Update selection to deselect voted NFTs
    this.selectedPowerStore.setSelectedNfts(remainingSelectedIds)
  }
}
